import React, { useContext, useMemo, useState } from 'react';
import { UserContext } from '../App';
import OwnerShell from './OwnerShell';
import { ADMIN_INSPECTOR_FEATURE_ENABLED, isAdminInspectorAllowed } from '../utils/adminInspector';

function tryJsonParse(s) {
  try { return { ok: true, value: JSON.parse(s) }; } catch (e) { return { ok: false, error: e }; }
}

function extractLikelyJson(raw) {
  const s = String(raw || '');
  const first = s.indexOf('{');
  const last = s.lastIndexOf('}');
  if (first >= 0 && last > first) return s.slice(first, last + 1);
  return s;
}

function normalizeInspectorPayload(parsed) {
  // Accept:
  // - full Lambda proxy response: { statusCode, headers, body: "json-string" }
  // - inner response body: { ownerSub, count, assets: [...] }
  // - raw "body" string pasted directly
  let obj = parsed;
  if (obj && typeof obj === 'object' && typeof obj.body === 'string') {
    const inner = tryJsonParse(obj.body);
    if (inner.ok) obj = inner.value;
  }
  return obj;
}

function assetsToRows(assets) {
  return (Array.isArray(assets) ? assets : []).map((a) => ({
    id: a?.id || '',
    category: a?.category || a?.payload?.typeCategory || '',
    type: a?.payload?.type || '',
    institution: a?.payload?.institution || '',
    accountNo: a?.payload?.accountNo || '',
    value: (a?.payload?.value ?? ''),
    notes: a?.payload?.notes || '',
    createdAt: a?.createdAt || '',
    deletedAt: a?.deletedAt || '',
  }));
}

function rowsToCsv(rows) {
  const header = ['id', 'category', 'type', 'institution', 'accountNo', 'value', 'notes', 'createdAt', 'deletedAt'];
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [
    header.join(','),
    ...rows.map((r) => header.map((k) => esc(r[k])).join(',')),
  ];
  return lines.join('\n');
}

export default function AdminInspectorFormatter() {
  const { currentUser } = useContext(UserContext);
  const [raw, setRaw] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState('');

  const allowed = isAdminInspectorAllowed(currentUser);

  const rows = useMemo(() => assetsToRows(result?.assets), [result]);
  const filteredRows = useMemo(() => {
    const q = String(filter || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => Object.values(r).some((v) => String(v ?? '').toLowerCase().includes(q)));
  }, [rows, filter]);

  if (!ADMIN_INSPECTOR_FEATURE_ENABLED) {
    return (
      <OwnerShell>
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '20px 20px 40px' }}>
          <div style={{ width: '100%', maxWidth: 720, background: '#fff', borderRadius: 16, padding: 20, boxShadow: 'var(--shadow-soft)' }}>
            <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 10 }}>Admin Inspector</div>
            <div style={{ color: '#444' }}>
              This page is disabled. Enable it with <code>REACT_APP_FEATURE_ADMIN_INSPECTOR=1</code>.
            </div>
          </div>
        </div>
      </OwnerShell>
    );
  }

  if (!allowed) {
    return (
      <OwnerShell>
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '20px 20px 40px' }}>
          <div style={{ width: '100%', maxWidth: 720, background: '#fff', borderRadius: 16, padding: 20, boxShadow: 'var(--shadow-soft)' }}>
            <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 10 }}>Admin Inspector</div>
            <div style={{ color: '#444' }}>
              Access denied. This tool is restricted to ProVault admins (and/or allowlisted emails).
            </div>
          </div>
        </div>
      </OwnerShell>
    );
  }

  const onParse = () => {
    setError('');
    setResult(null);
    const input = String(raw || '').trim();
    if (!input) {
      setError('Paste the Lambda response JSON first.');
      return;
    }

    const attempt1 = tryJsonParse(input);
    let parsed = attempt1.ok ? attempt1.value : null;
    if (!parsed) {
      const extracted = extractLikelyJson(input);
      const attempt2 = tryJsonParse(extracted);
      if (!attempt2.ok) {
        setError('Could not parse JSON. Paste the full Lambda response object (statusCode/headers/body) or the inner body JSON.');
        return;
      }
      parsed = attempt2.value;
    }

    const normalized = normalizeInspectorPayload(parsed);
    if (!normalized || typeof normalized !== 'object') {
      setError('Parsed JSON was not an object.');
      return;
    }
    if (!Array.isArray(normalized.assets)) {
      setError('Could not find an assets array. Make sure you pasted the Inspector response JSON.');
      return;
    }
    setResult({
      ownerSub: normalized.ownerSub || '',
      count: Number(normalized.count ?? normalized.assets.length ?? 0),
      assets: normalized.assets,
    });
  };

  const onCopyCsv = async () => {
    try {
      const csv = rowsToCsv(filteredRows);
      await navigator.clipboard.writeText(csv);
    } catch {
      // Clipboard may be denied; degrade silently.
    }
  };

  return (
    <OwnerShell>
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '20px 20px 40px' }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: 16, boxShadow: 'var(--shadow-soft)', marginBottom: 16 }}>
          <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 6 }}>Admin Inspector Formatter</div>
          <div style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>Paste Inspector Lambda output and view as a table.</div>

          <div style={{ fontWeight: 800, marginBottom: 8 }}>Paste Lambda response</div>
          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder='Paste the full Lambda response JSON (including "body"), or paste the inner body JSON.'
            style={{ width: '100%', minHeight: 180, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 12, padding: 12, borderRadius: 12, border: '1px solid var(--mist-300)' }}
          />
          {error && <div style={{ marginTop: 10, color: '#b00020', fontWeight: 700 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
            <button onClick={onParse} style={{ background: 'var(--sage-600)', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 14px', fontWeight: 800, cursor: 'pointer' }}>
              Parse
            </button>
            <button onClick={() => { setRaw(''); setResult(null); setError(''); setFilter(''); }} style={{ background: '#eee', color: '#222', border: 'none', borderRadius: 12, padding: '10px 14px', fontWeight: 700, cursor: 'pointer' }}>
              Clear
            </button>
          </div>
        </div>

        {result && (
          <div style={{ background: '#fff', borderRadius: 16, padding: 16, boxShadow: 'var(--shadow-soft)' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'baseline' }}>
                <div style={{ fontWeight: 900 }}>Owner:</div>
                <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 12 }}>{result.ownerSub || '(unknown)'}</div>
                <div style={{ color: '#555' }}>Assets: <b>{result.count}</b></div>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <input
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Filter…"
                  style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid var(--mist-300)', minWidth: 220 }}
                />
                <button onClick={onCopyCsv} style={{ background: '#29304a', color: '#fff', border: 'none', borderRadius: 12, padding: '10px 14px', fontWeight: 800, cursor: 'pointer' }}>
                  Copy CSV
                </button>
              </div>
            </div>

            <div style={{ overflowX: 'auto', border: '1px solid var(--mist-300)', borderRadius: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead style={{ background: 'var(--mist-200)' }}>
                  <tr>
                    {['Category', 'Type', 'Institution', 'Account #', 'Value', 'Notes', 'Created', 'Deleted', 'ID'].map((h) => (
                      <th key={h} style={{ textAlign: 'left', padding: '10px 10px', borderBottom: '1px solid var(--mist-300)', whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((r) => (
                    <tr key={r.id}>
                      <td style={{ padding: '10px 10px', borderBottom: '1px solid var(--mist-200)', whiteSpace: 'nowrap' }}>{r.category}</td>
                      <td style={{ padding: '10px 10px', borderBottom: '1px solid var(--mist-200)', whiteSpace: 'nowrap' }}>{r.type}</td>
                      <td style={{ padding: '10px 10px', borderBottom: '1px solid var(--mist-200)', whiteSpace: 'nowrap' }}>{r.institution}</td>
                      <td style={{ padding: '10px 10px', borderBottom: '1px solid var(--mist-200)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>{r.accountNo}</td>
                      <td style={{ padding: '10px 10px', borderBottom: '1px solid var(--mist-200)', whiteSpace: 'nowrap' }}>{String(r.value)}</td>
                      <td style={{ padding: '10px 10px', borderBottom: '1px solid var(--mist-200)' }}>{r.notes}</td>
                      <td style={{ padding: '10px 10px', borderBottom: '1px solid var(--mist-200)', whiteSpace: 'nowrap' }}>{r.createdAt}</td>
                      <td style={{ padding: '10px 10px', borderBottom: '1px solid var(--mist-200)', whiteSpace: 'nowrap' }}>{r.deletedAt}</td>
                      <td style={{ padding: '10px 10px', borderBottom: '1px solid var(--mist-200)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace', fontSize: 11 }}>{r.id}</td>
                    </tr>
                  ))}
                  {filteredRows.length === 0 && (
                    <tr>
                      <td colSpan={9} style={{ padding: 14, color: '#666' }}>No rows match your filter.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </OwnerShell>
  );
}

